import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../config/axios";
import { errorHandler, successHandler } from "../../../helpers/handlers";
import { logout } from "../auth";

const initialState = {
  user: null,
  error: null,
  employees: [],
  applications: [],
  ambassadorStats: null,
  loading: false,
  applicationsLoading: false,
  ambassadorStatsLoading: false,
  ambassadorActionLoading: false,
};

const userSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    setSelectedUser: (state, action) => {
      state.user = action.payload;
      state.applications = [];
      state.ambassadorStats = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserByEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.user = null;
      })
      .addCase(getUserByEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.error = null;
      })
      .addCase(getUserByEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateUserRole.pending, (state) => {
        state.loading = null;
        state.error = null;
      })
      .addCase(updateUserRole.fulfilled, (state, action) => {
        state.loading = false;
        state.user.role = action.payload.role;
        state.user.userType = action.payload.role;
        state.error = null;
      })
      .addCase(updateUserRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getEmployees.pending, (state) => {
        state.loading = null;
        state.error = null;
      })
      .addCase(getEmployees.fulfilled, (state, action) => {
        state.loading = false;
        state.employees = action.payload;
        state.error = null;
      })
      .addCase(getEmployees.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getUserApplications.pending, (state) => {
        state.applicationsLoading = true;
        state.error = null;
      })
      .addCase(getUserApplications.fulfilled, (state, action) => {
        state.applicationsLoading = false;
        state.applications = action.payload;
        state.error = null;
      })
      .addCase(getUserApplications.rejected, (state, action) => {
        state.applicationsLoading = false;
        state.error = action.payload;
      })

      .addCase(fetchUserAmbassadorStats.pending, (state) => {
        state.ambassadorStatsLoading = true;
        state.ambassadorStats = null;
      })
      .addCase(fetchUserAmbassadorStats.fulfilled, (state, action) => {
        state.ambassadorStatsLoading = false;
        state.ambassadorStats = action.payload;
      })
      .addCase(fetchUserAmbassadorStats.rejected, (state) => {
        state.ambassadorStatsLoading = false;
      })

      .addCase(assignUserAsAmbassador.pending, (state) => {
        state.ambassadorActionLoading = true;
      })
      .addCase(assignUserAsAmbassador.fulfilled, (state, action) => {
        state.ambassadorActionLoading = false;
        if (state.ambassadorStats) {
          state.ambassadorStats.isAmbassador = true;
          if (action.payload.code && !state.ambassadorStats.codes.find((c) => c.code === action.payload.code)) {
            state.ambassadorStats.codes.push({ code: action.payload.code, isActive: true, usageCount: 0, createdAt: null });
          }
        }
      })
      .addCase(assignUserAsAmbassador.rejected, (state) => {
        state.ambassadorActionLoading = false;
      })

      .addCase(removeUserAsAmbassador.pending, (state) => {
        state.ambassadorActionLoading = true;
      })
      .addCase(removeUserAsAmbassador.fulfilled, (state) => {
        state.ambassadorActionLoading = false;
        if (state.ambassadorStats) {
          state.ambassadorStats.isAmbassador = false;
          state.ambassadorStats.codes = state.ambassadorStats.codes.map((c) => ({ ...c, isActive: false }));
        }
      })
      .addCase(removeUserAsAmbassador.rejected, (state) => {
        state.ambassadorActionLoading = false;
      })

      .addCase(logout.fulfilled, () => initialState);
  },
});

export const getUserByEmail = createAsyncThunk(
  "auth/users/:email",
  async ({ userEmail }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin/auth/users/${userEmail}`);
      const user = response.data.payload;
      user.role = user.userType; // it is stored in the user table as userType

      if (user.email === "admin@purplebook.ng") {
        user.role = user.role || "admin";
      }

      return {
        user,
      };
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateUserRole = createAsyncThunk(
  "/admin/auth/roles/:userId",
  async ({ userId, role }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/admin/auth/roles/${userId}`, {
        role,
      });
      successHandler(response.data.message);
      return {
        role,
      };
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

export const getEmployees = createAsyncThunk(
  "auth/users/employees",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin/auth/users/employees`);
      return response.data.payload;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

export const getUserApplications = createAsyncThunk(
  "users/applications",
  async ({ userId }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin/applications?userId=${userId}`);
      return response.data.payload.data;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchUserAmbassadorStats = createAsyncThunk(
  "users/ambassadorStats",
  async ({ userId }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin/referrals/users/${userId}/stats`);
      return response.data.payload;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const assignUserAsAmbassador = createAsyncThunk(
  "users/assignAmbassador",
  async ({ userId, customCode, codePrefix }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/admin/referrals/users/${userId}/ambassador`, {
        customCode: customCode || undefined,
        codePrefix: codePrefix || undefined,
      });
      successHandler("Ambassador status assigned");
      return response.data.payload;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

export const removeUserAsAmbassador = createAsyncThunk(
  "users/removeAmbassador",
  async ({ userId }, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/admin/referrals/users/${userId}/ambassador`);
      successHandler("Ambassador status removed");
      return response.data.payload;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

export const { setSelectedUser } = userSlice.actions;

export default userSlice.reducer;
